import { useCallback, useMemo, useRef, useState } from 'react'
import { retryGetAccessToken } from '@towns/privy'
import { AccountAbstractionConfig } from '@towns/userops'
import { townsEnv } from '@towns-protocol/sdk'
import { BaseChainConfig, RiverChainConfig, web3Env } from '@towns-protocol/web3'
import { getBaseGatewayChain, getCustomBaseChain, getCustomRiverChain } from 'customChains'
import { IChainConfig } from 'use-towns-client'
import { env, VITE_ENV_OPTIONS } from 'utils'
import { Chain } from 'viem'

const TOWNS_DEV_ENV = 'TOWNS_DEV_ENV'

const townsEnvInst = townsEnv(VITE_ENV_OPTIONS)

export interface TownsEnvironmentInfo {
    id: string
    name: string
    baseChain: Chain
    gatewayBaseChain: Chain
    baseChainConfig: BaseChainConfig
    riverChain: IChainConfig
    riverChainConfig: RiverChainConfig
    accountAbstractionConfig?: AccountAbstractionConfig
}

function makeEnvironments(): TownsEnvironmentInfo[] {
    const retVal: TownsEnvironmentInfo[] = []
    const fetchAccessTokenFn = () => retryGetAccessToken(3)
    const web3EnvInst = web3Env(VITE_ENV_OPTIONS)
    // add the web3 environments
    for (const riverEnv of web3EnvInst.getDeploymentIds()) {
        const deployment = web3EnvInst.getDeployment(riverEnv)
        // don't add the default env
        // get the chains
        const baseChain = getCustomBaseChain(deployment.base.chainId)
        const gatewayBaseChain = getBaseGatewayChain(deployment.base.chainId)
        // don't add chain if we haven't predefined a custom base chain
        if (!baseChain) {
            continue
        }
        const riverChain = getCustomRiverChain(deployment.river.chainId)
        // don't add chain if we haven't predefined a custom river chain
        if (!riverChain) {
            continue
        }
        const envInfo: TownsEnvironmentInfo = {
            id: riverEnv,
            name: riverEnv,
            baseChain,
            gatewayBaseChain,
            baseChainConfig: deployment.base,
            riverChain,
            riverChainConfig: deployment.river,
        }

        // Account abstraction works on beta or omega.
        if (riverEnv === 'omega' || riverEnv == 'beta') {
            envInfo.accountAbstractionConfig = {
                // the same alchemy rpc url handles both rpc and bundler endpoints
                aaRpcUrl: baseChain.rpcUrls.default.http[0],
                bundlerUrl: baseChain.rpcUrls.default.http[0],
                paymasterProxyUrl: env.VITE_AA_PAYMASTER_PROXY_URL,
                paymasterProxyAuthSecret: env.VITE_AUTH_WORKER_HEADER_SECRET,
                fetchAccessTokenFn,
                newAccountImplementationType: env.VITE_NEW_SMART_ACCOUNT_IMPLEMENTATION_TYPE,
                usePimlicoPaymaster: env.VITE_USE_PIMLICO_PAYMASTER,
            }
        }
        // Account abstraction only works on local nodes if running geth, not anvil
        else {
            envInfo.accountAbstractionConfig = {
                aaRpcUrl: 'http://localhost:8545',
                bundlerUrl: 'http://localhost:43370',
                paymasterProxyUrl: 'http://localhost:8686',
                paymasterProxyAuthSecret: env.VITE_AUTH_WORKER_HEADER_SECRET,
                fetchAccessTokenFn,
                newAccountImplementationType: env.VITE_NEW_SMART_ACCOUNT_IMPLEMENTATION_TYPE,
                usePimlicoPaymaster: env.VITE_USE_PIMLICO_PAYMASTER,
            }
        }

        retVal.push(envInfo)
    }
    return retVal
}

export const ENVIRONMENTS = makeEnvironments()
if (!ENVIRONMENTS.length) {
    throw new Error('No environments defined')
}
const DEFAULT_ENVIRONMENT = ENVIRONMENTS.find((x) => x.id === env.VITE_RIVER_ENV) ?? ENVIRONMENTS[0]

export type UseEnvironmentReturn = ReturnType<typeof useEnvironment>

export function useEnvironment() {
    const _environmentId = useRef<{ value: string | undefined } | undefined>(undefined)
    if (env.DEV && !_environmentId.current) {
        _environmentId.current = {
            value: localStorage.getItem(TOWNS_DEV_ENV) as string | undefined,
        }
    }

    const [environmentId, _setEnvironmentId] = useState<string | undefined>(
        _environmentId.current?.value,
    )

    const clearEnvironment = useCallback(() => {
        localStorage.removeItem('RIVER_RPC_URL')
        localStorage.removeItem(TOWNS_DEV_ENV)
        _setEnvironmentId(undefined)
    }, [])

    const setEnvironment = useCallback((newValue: string) => {
        _setEnvironmentId(newValue)
        localStorage.removeItem('RIVER_RPC_URL')
        localStorage.setItem(TOWNS_DEV_ENV, newValue)
        // after setEnvironment is called the page needs to be refreshed
    }, [])

    const environmentInfo = useMemo(
        () => ENVIRONMENTS.find((e) => e.id === environmentId) ?? DEFAULT_ENVIRONMENT,
        [environmentId],
    )

    return useMemo(() => {
        return {
            ...environmentInfo,
            townsEnv: townsEnvInst,
            setEnvironment,
            clearEnvironment,
        }
    }, [environmentInfo, setEnvironment, clearEnvironment])
}
